import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';
import ApprovalStatusCellRenderer from '@casops/components/tables/cells/approval-status/ApprovalStatusCellRenderer';
import ConfigurationGroupIdCellRenderer from '@casops/components/tables/cells/configuration-group/ConfigurationGroupIdCellRenderer';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';
import i18n from '@casops/i18n';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { ModelName } from '@casops/store/api';
import {
  AssetApprovalBasicModel,
  useDeleteApprovalMutation,
} from '@casops/store/apis/assetApprovalsApi';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Model = AssetApprovalBasicModel;
const permission = Permission.AssetManagement;
const secondaryModelName = ModelName.Asset;
const sidePanel = SidePanel.ManageAssetApproval;

const getItemName = ({ an: assetName, did: deviceId }: Model): string => {
  return deviceId !== null ? i18n.t('PendingApprovalItemName', { assetName, deviceId }) : assetName;
};

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName: secondaryModelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem: true,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteApprovalMutation,
    getItemName,
  },
};

export const useApprovalColDefs = (): ColDef<Model>[] => {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      {
        field: 'ts',
        headerName: t('Fields.Timestamp'),
        sort: 'desc',
        minWidth: 180,
        ...absoluteTimeColDef,
      },
      {
        field: 'an',
        headerName: t('Fields.AssetName'),
        minWidth: 130,
      },
      { field: 'did', headerName: t('Fields.DeviceId'), minWidth: 150 },
      {
        field: 'cfgId',
        headerName: t('Fields.ConfigGroup'),
        cellRenderer: ConfigurationGroupIdCellRenderer,
        minWidth: 250,
      },
      {
        field: 'by',
        headerName: t('Fields.CreatedBy'),
        minWidth: 250,
      },
      {
        field: 'sts',
        headerName: t('Fields.Status'),
        cellRenderer: ApprovalStatusCellRenderer,
        minWidth: 180,
      },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions, absoluteTimeColDef]);

  return columnDefs;
};
